import React from 'react';
import { BonusSummaryData, CapBonusPV, CapBonusHP } from '../types';

type ProductType = 'Photovoltaic' | 'Heatpump' | 'Photovoltaic + Heatpump';

interface SavingsSummaryProps {
  productType?: ProductType;
  bonusSummaryData?: BonusSummaryData;
}

interface SavingsSummaryItem {
  label: string;
  value: string;
  scadenza?: string | null;
  description?: string;
}

const BONUS_DESCRIPTIONS: Record<string, string> = {
  Ecobonus: 'Recuperi il 50% della spesa come detrazione fiscale, ripartita in 10 anni.',
  'PNRR CER Enpal':
    'Ricevi un accredito in denaro sul conto dopo attivazione della pratica - richiedilo prima della scadenza indicata.',
  'Risparmio in Bolletta': 'Da subito paghi meno corrente perché una parte la produci e la consumi tu.',
  'Rivendita Energia': "L'energia che non usi non si perde: la immetti in rete e ti viene pagata.",
  'Rivendita Surplus CER':
    "Con la Comunità Energetica Enpal, l'energia condivisa genera un extra ricavo/incentivo aggiuntivo.",
  'Conto Termico': "Ti applichiamo subito uno sconto sul prezzo, perché anticipiamo noi l'incentivo del GSE.",
  'Guadagni Trading Energia':
    "Attiva il servizio e l'impianto massimizza i guadagni, scegliendo automaticamente quando usare e quando vendere l'energia.",
};

function formatCurrency(amount: number): string {
  const formatted = new Intl.NumberFormat('de-DE').format(amount);
  return `+${formatted}€`;
}

function formatExpiryDate(scadenza?: string | null): string {
  if (!scadenza) return '-';
  const parts = scadenza.split('.');
  if (parts.length === 3) {
    return `${parts[0]}/${parts[1]}/${parts[2].slice(-2)}`;
  }
  return scadenza;
}

function generatePVItems(pv: CapBonusPV): SavingsSummaryItem[] {
  const items: SavingsSummaryItem[] = [];

  if (pv.ecobonus !== null && pv.ecobonus > 0) {
    items.push({
      label: 'Ecobonus',
      value: formatCurrency(pv.ecobonus),
      description: BONUS_DESCRIPTIONS.Ecobonus,
    });
  }
  if (pv.pnrrCer !== null && pv.pnrrCer > 0) {
    items.push({
      label: 'PNRR CER Enpal',
      value: formatCurrency(pv.pnrrCer),
      scadenza: pv.pnrrScadenza,
      description: BONUS_DESCRIPTIONS['PNRR CER Enpal'],
    });
  }
  if (pv.bonusRegionale !== null && pv.bonusRegionale > 0) {
    items.push({
      label: 'Bonus Regionale',
      value: formatCurrency(pv.bonusRegionale),
    });
  }
  if (pv.bonusProvinciale !== null && pv.bonusProvinciale > 0) {
    items.push({
      label: 'Bonus Provinciale',
      value: formatCurrency(pv.bonusProvinciale),
    });
  }
  if (pv.risparmioBolletta !== null && pv.risparmioBolletta > 0) {
    items.push({
      label: 'Risparmio in Bolletta',
      value: formatCurrency(pv.risparmioBolletta),
      description: BONUS_DESCRIPTIONS['Risparmio in Bolletta'],
    });
  }
  if (pv.rivenditaEnergia !== null && pv.rivenditaEnergia > 0) {
    items.push({
      label: 'Rivendita Energia',
      value: formatCurrency(pv.rivenditaEnergia),
      description: BONUS_DESCRIPTIONS['Rivendita Energia'],
    });
  }
  if (pv.rivenditaSurplus !== null && pv.rivenditaSurplus > 0) {
    items.push({
      label: 'Rivendita Surplus CER',
      value: formatCurrency(pv.rivenditaSurplus),
      description: BONUS_DESCRIPTIONS['Rivendita Surplus CER'],
    });
  }

  return items;
}

function generateHPItems(hp: CapBonusHP): SavingsSummaryItem[] {
  const items: SavingsSummaryItem[] = [];

  if (hp.contoTermico !== null && hp.contoTermico > 0) {
    items.push({
      label: 'Conto Termico',
      value: formatCurrency(hp.contoTermico),
      description: BONUS_DESCRIPTIONS['Conto Termico'],
    });
  }
  if (hp.ecobonus !== null && hp.ecobonus > 0) {
    items.push({
      label: 'Ecobonus',
      value: formatCurrency(hp.ecobonus),
      description: BONUS_DESCRIPTIONS.Ecobonus,
    });
  }
  if (hp.bonusRegionale !== null && hp.bonusRegionale > 0) {
    items.push({
      label: 'Bonus Regionale',
      value: formatCurrency(hp.bonusRegionale),
    });
  }
  if (hp.bonusProvinciale !== null && hp.bonusProvinciale > 0) {
    items.push({
      label: 'Bonus Provinciale',
      value: formatCurrency(hp.bonusProvinciale),
    });
  }
  if (hp.risparmioBolletta !== null && hp.risparmioBolletta > 0) {
    items.push({
      label: 'Risparmio in Bolletta',
      value: formatCurrency(hp.risparmioBolletta),
      description: BONUS_DESCRIPTIONS['Risparmio in Bolletta'],
    });
  }

  return items;
}

function calculateTotal(bonusSummaryData?: BonusSummaryData, productType?: ProductType): number {
  if (!bonusSummaryData) return 0;

  let total = 0;
  const includePV = productType === 'Photovoltaic' || productType === 'Photovoltaic + Heatpump';
  const includeHP = productType === 'Heatpump' || productType === 'Photovoltaic + Heatpump';

  if (includePV && bonusSummaryData.pv) {
    const pv = bonusSummaryData.pv;
    if (pv.ecobonus) total += pv.ecobonus;
    if (pv.pnrrCer) total += pv.pnrrCer;
    if (pv.bonusRegionale) total += pv.bonusRegionale;
    if (pv.bonusProvinciale) total += pv.bonusProvinciale;
    if (pv.risparmioBolletta) total += pv.risparmioBolletta;
    if (pv.rivenditaEnergia) total += pv.rivenditaEnergia;
    if (pv.rivenditaSurplus) total += pv.rivenditaSurplus;
  }

  if (includeHP && bonusSummaryData.hp) {
    const hp = bonusSummaryData.hp;
    if (hp.contoTermico) total += hp.contoTermico;
    if (hp.ecobonus) total += hp.ecobonus;
    if (hp.bonusRegionale) total += hp.bonusRegionale;
    if (hp.bonusProvinciale) total += hp.bonusProvinciale;
    if (hp.risparmioBolletta) total += hp.risparmioBolletta;
  }

  return total;
}

function generateSavingsSummary(
  bonusSummaryData?: BonusSummaryData,
  productType?: ProductType
): { pvItems: SavingsSummaryItem[]; hpItems: SavingsSummaryItem[]; total: number } {
  if (!bonusSummaryData) {
    return { pvItems: [], hpItems: [], total: 0 };
  }

  let pvItems: SavingsSummaryItem[] = [];
  let hpItems: SavingsSummaryItem[] = [];

  const includePV = productType === 'Photovoltaic' || productType === 'Photovoltaic + Heatpump';
  const includeHP = productType === 'Heatpump' || productType === 'Photovoltaic + Heatpump';

  if (includePV && bonusSummaryData.pv) {
    pvItems = generatePVItems(bonusSummaryData.pv);
  }
  if (includeHP && bonusSummaryData.hp) {
    hpItems = generateHPItems(bonusSummaryData.hp);
  }

  return { pvItems, hpItems, total: calculateTotal(bonusSummaryData, productType) };
}

const SectionDivider: React.FC<{ label: string }> = ({ label }) => (
  <div className="my-5 flex items-center gap-3">
    <div className="h-px flex-1 bg-slate-200/90" />
    <span className="whitespace-nowrap rounded-full border border-enpal-downriver/15 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-enpal-stone-grey">
      {label}
    </span>
    <div className="h-px flex-1 bg-slate-200/90" />
  </div>
);

const MobileBonusCard: React.FC<SavingsSummaryItem> = ({ label, value, scadenza, description }) => (
  <div className="mb-3 rounded-3xl border border-enpal-downriver/10 bg-white p-4 shadow-[0_10px_24px_rgba(7,37,67,0.08)] md:hidden">
    <div className="flex items-start justify-between gap-3">
      <span className="text-sm font-semibold text-enpal-downriver">{label}</span>
      <span className="shrink-0 rounded-full bg-enpal-cta-yellow/35 px-2.5 py-1 text-sm font-semibold text-enpal-downriver">
        {value}
      </span>
    </div>
    {scadenza && <p className="mt-2 text-xs font-semibold text-rose-600">Scade: {formatExpiryDate(scadenza)}</p>}
    {description && <p className="mt-2 text-xs text-slate-500">{description}</p>}
  </div>
);

export const SavingsSummary: React.FC<SavingsSummaryProps> = ({ productType, bonusSummaryData }) => {
  const summary = generateSavingsSummary(bonusSummaryData, productType);

  if (summary.pvItems.length === 0 && summary.hpItems.length === 0) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">
        <p>Nessun bonus disponibile.</p>
      </div>
    );
  }

  const renderDesktopRow = (item: SavingsSummaryItem, index: number) => (
    <div
      key={`${item.label}-${index}`}
      className="hidden grid-cols-[minmax(150px,1fr)_minmax(110px,auto)_minmax(100px,auto)_minmax(220px,2fr)] items-center gap-4 rounded-2xl border border-slate-200/80 bg-white px-3 py-3 text-sm shadow-[0_4px_12px_rgba(7,37,67,0.04)] md:grid"
    >
      <span className="font-medium text-enpal-downriver">{item.label}</span>
      <span className="text-right font-semibold text-enpal-downriver">{item.value}</span>
      <span className="text-right text-xs font-semibold text-rose-600/90">{formatExpiryDate(item.scadenza)}</span>
      <span className="text-xs text-slate-500">{item.description || ''}</span>
    </div>
  );

  return (
    <div className="rounded-[30px] border border-enpal-downriver/10 bg-gradient-to-b from-white via-slate-50 to-white p-4 text-left shadow-[0_18px_40px_rgba(7,37,67,0.08)] sm:p-5">
      <div className="mb-4 rounded-2xl border border-enpal-yellow-sea/35 bg-enpal-sunshine-yellow/40 px-4 py-3 text-center">
        <p className="text-sm font-semibold text-enpal-downriver/90">Risparmio in 10 anni:</p>
      </div>

      <div className="mb-3 hidden grid-cols-[minmax(150px,1fr)_minmax(110px,auto)_minmax(100px,auto)_minmax(220px,2fr)] gap-4 px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 md:grid">
        <span>VANTAGGIO</span>
        <span className="text-right">IMPORTO</span>
        <span className="text-right">SCADENZA</span>
        <span>DESCRIZIONE</span>
      </div>

      {summary.pvItems.length > 0 && (
        <>
          {summary.hpItems.length > 0 && <SectionDivider label="Fotovoltaico" />}
          <div className="space-y-2">
            {summary.pvItems.map((item, index) => (
              <React.Fragment key={`pv-${item.label}-${index}`}>
                <MobileBonusCard {...item} />
                {renderDesktopRow(item, index)}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {summary.hpItems.length > 0 && (
        <>
          {summary.pvItems.length > 0 && <SectionDivider label="Pompa di calore" />}
          <div className="space-y-2">
            {summary.hpItems.map((item, index) => (
              <React.Fragment key={`hp-${item.label}-${index}`}>
                <MobileBonusCard {...item} />
                {renderDesktopRow(item, index)}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      <div className="my-5 border-t border-dashed border-slate-300" />

      <div className="mb-2 flex items-center justify-between rounded-2xl border border-enpal-yellow-sea/40 bg-enpal-cta-yellow/35 p-4 text-sm font-bold text-enpal-downriver md:hidden">
        <span>Risparmio Totale</span>
        <span>{formatCurrency(summary.total)}</span>
      </div>

      <div className="hidden grid-cols-[minmax(150px,1fr)_minmax(110px,auto)_minmax(100px,auto)_minmax(220px,2fr)] items-center gap-4 rounded-2xl border border-enpal-yellow-sea/40 bg-enpal-cta-yellow/30 px-4 py-3 text-base font-bold text-enpal-downriver md:grid">
        <span>Risparmio Totale</span>
        <span className="text-right">{formatCurrency(summary.total)}</span>
        <span />
        <span />
      </div>

      <div className="mt-4 rounded-2xl border border-enpal-downriver/10 bg-white p-3 shadow-[0_8px_16px_rgba(7,37,67,0.05)] md:grid md:grid-cols-[minmax(150px,1fr)_minmax(110px,auto)_minmax(100px,auto)_minmax(220px,2fr)] md:items-center md:gap-4 md:border-0 md:bg-transparent md:p-0 md:shadow-none">
        <div className="flex items-center justify-between gap-3 md:contents">
          <span className="text-sm text-enpal-downriver">Guadagni Trading Energia</span>
          <span className="shrink-0 text-sm font-semibold text-rose-600 md:text-right">Scoprilo in chiamata</span>
        </div>
        <span className="hidden md:block" />
        <span className="mt-2 block text-xs text-slate-500 md:mt-0 md:block">
          {BONUS_DESCRIPTIONS['Guadagni Trading Energia']}
        </span>
      </div>
    </div>
  );
};

export default SavingsSummary;
